// ─── Translation Key Types ────────────────────────────────────────────────────

export interface Translations {
  buttons: ButtonTranslations;
  reminders: ReminderTranslations;
  petMoods: PetMoodTranslations;
  bot: BotTranslations;
  general: GeneralTranslations;
  errors: ErrorTranslations;
}

export interface ButtonTranslations {
  done: string;
  snooze10: string;
  snooze30: string;
  skip: string;
  addChild: string;
  addRoutine: string;
  viewPet: string;
  settings: string;
  cancel: string;
  confirm: string;
  back: string;
  next: string;
  save: string;
  delete: string;
  edit: string;
  enable: string;
  disable: string;
}

export interface ReminderTranslations {
  timeFor: (childName: string, stepTitle: string) => string;
  duration: (minutes: number) => string;
  snoozedUntil: (time: string) => string;
  markedDone: string;
  skipped: string;
  missed: string;
  streakContinued: (days: number) => string;
}

export interface PetMoodTranslations {
  ecstatic: string;
  happy: string;
  content: string;
  neutral: string;
  sad: string;
  unhappy: string;
  distressed: string;
}

export interface BotTranslations {
  welcome: (firstName: string) => string;
  welcomeBack: (firstName: string) => string;
  helpText: string;
  familyCreated: (familyName: string) => string;
  childAdded: (childName: string) => string;
  routineCreated: (routineName: string) => string;
  noFamily: string;
  notRegistered: string;
}

export interface GeneralTranslations {
  yes: string;
  no: string;
  loading: string;
  success: string;
  error: string;
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  sunday: string;
}

export interface ErrorTranslations {
  unknown: string;
  notFound: string;
  unauthorized: string;
  validationFailed: string;
  telegramError: string;
  databaseError: string;
}

// ─── German Translations ──────────────────────────────────────────────────────

export const de: Translations = {
  buttons: {
    done: "✅ Erledigt",
    snooze10: "⏰ 10 Min. später",
    snooze30: "⏰ 30 Min. später",
    skip: "⏭ Überspringen",
    addChild: "➕ Kind hinzufügen",
    addRoutine: "➕ Routine hinzufügen",
    viewPet: "🐱 Meine Katze",
    settings: "⚙️ Einstellungen",
    cancel: "❌ Abbrechen",
    confirm: "✅ Bestätigen",
    back: "⬅️ Zurück",
    next: "➡️ Weiter",
    save: "💾 Speichern",
    delete: "🗑️ Löschen",
    edit: "✏️ Bearbeiten",
    enable: "✅ Aktivieren",
    disable: "⛔ Deaktivieren",
  },

  reminders: {
    timeFor: (childName, stepTitle) =>
      `🔔 Zeit für ${childName}'s ${stepTitle}!`,
    duration: (minutes) =>
      minutes === 1 ? `Dauer: 1 Minute` : `Dauer: ca. ${minutes} Minuten`,
    snoozedUntil: (time) => `⏰ Erinnerung auf ${time} Uhr verschoben.`,
    markedDone: "✅ Super gemacht! Deine Katze freut sich!",
    skipped: "⏭ Schritt übersprungen.",
    missed: "😿 Schritt verpasst. Deine Katze ist traurig.",
    streakContinued: (days) =>
      days === 1
        ? `🔥 1 Tag in Folge – weiter so!`
        : `🔥 ${days} Tage in Folge – fantastisch!`,
  },

  petMoods: {
    ecstatic: "überglücklich 🤩",
    happy: "fröhlich 😸",
    content: "zufrieden 😺",
    neutral: "entspannt 😐",
    sad: "traurig 😿",
    unhappy: "unglücklich 😾",
    distressed: "verzweifelt 🙀",
  },

  bot: {
    welcome: (firstName) =>
      `Hallo ${firstName}! 👋\n\nWillkommen bei MiezMission – dein Familien-Routinen-Assistent mit Katzenfaktor! 🐱\n\nTippe /help für alle Befehle.`,
    welcomeBack: (firstName) =>
      `Willkommen zurück, ${firstName}! 😸`,
    helpText: [
      "📋 *Verfügbare Befehle:*",
      "",
      "/start – Bot starten",
      "/help – Diese Hilfe anzeigen",
      "/familie – Familienübersicht",
      "/kind – Kind verwalten",
      "/routine – Routinen verwalten",
      "/katze – Katzenstand anzeigen",
      "/einstellungen – Einstellungen",
    ].join("\n"),
    familyCreated: (familyName) =>
      `✅ Familie "${familyName}" wurde erfolgreich erstellt!`,
    childAdded: (childName) =>
      `✅ ${childName} wurde zur Familie hinzugefügt!`,
    routineCreated: (routineName) =>
      `✅ Routine "${routineName}" wurde erstellt!`,
    noFamily: "Du bist noch keiner Familie zugeordnet. Erstelle eine mit /start.",
    notRegistered:
      "Du bist noch nicht registriert. Starte den Bot mit /start.",
  },

  general: {
    yes: "Ja",
    no: "Nein",
    loading: "Laden…",
    success: "Erfolgreich!",
    error: "Fehler",
    monday: "Montag",
    tuesday: "Dienstag",
    wednesday: "Mittwoch",
    thursday: "Donnerstag",
    friday: "Freitag",
    saturday: "Samstag",
    sunday: "Sonntag",
  },

  errors: {
    unknown: "Ein unbekannter Fehler ist aufgetreten. Bitte versuche es erneut.",
    notFound: "Der angeforderte Inhalt wurde nicht gefunden.",
    unauthorized: "Du hast keine Berechtigung für diese Aktion.",
    validationFailed: "Ungültige Eingabe. Bitte überprüfe deine Angaben.",
    telegramError: "Fehler beim Senden der Telegram-Nachricht.",
    databaseError: "Datenbankfehler. Bitte versuche es später erneut.",
  },
};

// ─── Default export ───────────────────────────────────────────────────────────

export const translations = { de };
export type SupportedLocale = keyof typeof translations;

export function t(locale: SupportedLocale = "de"): Translations {
  return translations[locale];
}
